(() => {
  const GLOBAL = 'BasicHeroKiimaMotionV5';
  const SELECTOR = '[data-basic-hero-kiima]';
  const LEGACY_GLOBALS = [
    'BasicHeroKiimaMotionV2',
    'BasicHeroKiimaMotionV3',
    'BasicHeroKiimaMotionV4',
    GLOBAL
  ];

  LEGACY_GLOBALS.forEach((name) => {
    if (window[name]?.destroyAll) window[name].destroyAll(document);
  });

  const instances = new WeakMap();
  const mobileVideoQuery = window.matchMedia('(max-width: 899px)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function initHero(root) {
    if (!root || instances.has(root)) return;

    const video = root.querySelector('[data-basic-hero-video]');
    const stage = root.querySelector('.basic-hero-kiima__stage');
    const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const constrainedNetwork = Boolean(connection && connection.saveData);
    const scrubEnabled = root.dataset.scrub === 'true';
    const disposers = [];

    let duration = 0;
    let scrollRange = 1;
    let progressFrame = 0;
    let seekFrame = 0;
    let metricsFrame = 0;
    let targetTime = 0;
    let lastSeekAt = 0;
    let ready = false;
    let loadStarted = false;
    let observer = null;
    let resizeObserver = null;
    let retryCount = 0;
    let retryTimer = 0;
    let paintFallbackTimer = 0;

    root.dataset.motionReady = 'true';
    root.dataset.motionState = 'initializing';

    const sourceForViewport = () => {
      const desktop = root.dataset.videoSrcDesktop || '';
      const mobile = root.dataset.videoSrcMobile || desktop;
      return mobileVideoQuery.matches ? mobile : desktop;
    };

    const listen = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      disposers.push(() => target.removeEventListener(event, handler, options));
    };

    function getStickyTop() {
      const header = document.querySelector('.shopify-section-header, header');
      if (!header) return 0;

      return clamp(header.getBoundingClientRect().bottom, 0, window.innerHeight * 0.35);
    }

    function updateStickyTop() {
      const stickyTop = getStickyTop();
      root.style.setProperty('--hero-sticky-top', `${stickyTop.toFixed(2)}px`);
      return stickyTop;
    }

    function updateMetrics() {
      metricsFrame = 0;
      updateStickyTop();
      scrollRange = Math.max(1, root.offsetHeight - window.innerHeight);
    }

    function requestMetricsUpdate() {
      if (metricsFrame) return;
      metricsFrame = requestAnimationFrame(() => {
        updateMetrics();
        requestUpdate();
      });
    }

    function getHeroProgress() {
      if (!scrubEnabled) return 0;

      updateStickyTop();
      const rootRect = root.getBoundingClientRect();
      const stageRect = stage.getBoundingClientRect();
      const remainingStickyDistance = rootRect.bottom - stageRect.bottom;

      return clamp(1 - remainingStickyDistance / scrollRange, 0, 1);
    }

    function setVisualProgress(heroProgress) {
      root.style.setProperty('--hero-progress', heroProgress.toFixed(4));
      root.style.setProperty('--hero-cue-opacity', Math.max(0, 1 - heroProgress * 8).toFixed(3));
    }

    function revealPaintedFrame() {
      if (!video || ready || reducedQuery.matches || constrainedNetwork || video.readyState < 2) return;
      if (Math.abs(video.currentTime - targetTime) > 0.12) return;

      const reveal = () => {
        if (ready || reducedQuery.matches || constrainedNetwork) return;
        ready = true;
        window.clearTimeout(paintFallbackTimer);
        paintFallbackTimer = 0;
        root.classList.remove('is-static');
        root.classList.add('is-video-ready');
        root.dataset.motionState = 'ready';
      };

      paintFallbackTimer = window.setTimeout(reveal, 180);

      if ('requestVideoFrameCallback' in video) {
        video.requestVideoFrameCallback(reveal);
      } else {
        requestAnimationFrame(() => requestAnimationFrame(reveal));
      }
    }

    function applyTargetTime(now) {
      seekFrame = 0;
      if (!video || !scrubEnabled || duration <= 0 || video.readyState < 1) return;

      const delta = Math.abs(video.currentTime - targetTime);
      if (delta <= 0.018) {
        revealPaintedFrame();
        return;
      }

      if (now - lastSeekAt < 42) {
        seekFrame = requestAnimationFrame(applyTargetTime);
        return;
      }

      lastSeekAt = now;
      root.dataset.motionState = ready ? 'scrubbing' : 'seeking';

      try {
        video.currentTime = targetTime;
      } catch (_) {
        root.dataset.motionState = 'seek-pending';
      }
    }

    function requestSeek() {
      if (!seekFrame) seekFrame = requestAnimationFrame(applyTargetTime);
    }

    function updateProgress() {
      progressFrame = 0;

      const heroProgress = getHeroProgress();
      setVisualProgress(heroProgress);

      if (ready) {
        root.dataset.motionState = 'ready';
      }

      if (duration > 0) {
        const safeStart = 0.04;
        const scrubEnd = Math.max(safeStart, Math.min(9, duration - 0.08));
        targetTime = safeStart + heroProgress * (scrubEnd - safeStart);
        requestSeek();
      }
    }

    function requestUpdate() {
      if (!progressFrame) progressFrame = requestAnimationFrame(updateProgress);
    }

    function showStaticPoster(state = 'static') {
      if (video) video.pause();
      ready = false;
      root.classList.remove('is-video-ready');
      root.classList.add('is-static');
      root.dataset.motionState = state;
    }

    function resetVideoForReload() {
      ready = false;
      duration = 0;
      loadStarted = false;
      root.classList.remove('is-video-ready', 'is-static');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    }

    function loadVideo() {
      const source = sourceForViewport();
      if (loadStarted || !video || !source || reducedQuery.matches || constrainedNetwork) return;

      loadStarted = true;
      root.dataset.motionState = 'loading';
      video.preload = 'auto';
      video.muted = true;
      video.defaultMuted = true;
      video.src = source;
      video.load();
    }

    function onVideoError() {
      if (retryCount < 1 && sourceForViewport()) {
        retryCount += 1;
        root.dataset.motionState = 'retrying';
        resetVideoForReload();
        retryTimer = window.setTimeout(loadVideo, 480);
        return;
      }

      showStaticPoster('error');
    }

    function onMotionPreferenceChange(event) {
      if (event.matches) {
        showStaticPoster('reduced-motion');
        return;
      }

      resetVideoForReload();
      retryCount = 0;
      requestMetricsUpdate();
      loadVideo();
    }

    function onVideoViewportChange() {
      if (!video) return;
      const nextSource = sourceForViewport();
      const activeSource = video.getAttribute('src') || '';
      if (!nextSource || nextSource === activeSource) return;

      resetVideoForReload();
      retryCount = 0;
      requestMetricsUpdate();
      loadVideo();
    }

    function destroy() {
      if (observer) observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      if (progressFrame) cancelAnimationFrame(progressFrame);
      if (seekFrame) cancelAnimationFrame(seekFrame);
      if (metricsFrame) cancelAnimationFrame(metricsFrame);
      if (retryTimer) window.clearTimeout(retryTimer);
      if (paintFallbackTimer) window.clearTimeout(paintFallbackTimer);
      disposers.forEach((dispose) => dispose());
      if (video) video.pause();
      root.style.removeProperty('--hero-progress');
      root.style.removeProperty('--hero-cue-opacity');
      root.style.removeProperty('--hero-sticky-top');
      delete root.dataset.motionReady;
      delete root.dataset.motionState;
      instances.delete(root);
    }

    instances.set(root, destroy);
    updateMetrics();
    setVisualProgress(getHeroProgress());

    if (video) {
      listen(video, 'loadedmetadata', () => {
        duration = Number.isFinite(video.duration) ? video.duration : 0;
        updateProgress();
      });
      listen(video, 'loadeddata', () => {
        requestUpdate();
        requestSeek();
        revealPaintedFrame();
      });
      listen(video, 'canplay', () => {
        requestUpdate();
        requestSeek();
        revealPaintedFrame();
      });
      listen(video, 'seeked', () => {
        if (Math.abs(video.currentTime - targetTime) > 0.018) {
          requestSeek();
        } else {
          revealPaintedFrame();
        }
      });
      listen(video, 'error', onVideoError);
    }

    listen(reducedQuery, 'change', onMotionPreferenceChange);
    listen(mobileVideoQuery, 'change', onVideoViewportChange);
    listen(window, 'scroll', requestUpdate, { passive: true });
    listen(window, 'resize', requestMetricsUpdate, { passive: true });
    listen(window, 'pageshow', requestMetricsUpdate, { passive: true });
    listen(window, 'load', requestMetricsUpdate, { passive: true, once: true });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestMetricsUpdate);
      resizeObserver.observe(root);
      if (stage) resizeObserver.observe(stage);
    }

    if (!video || !sourceForViewport()) {
      showStaticPoster('missing-video');
      return;
    }

    if (reducedQuery.matches) {
      showStaticPoster('reduced-motion');
      return;
    }

    if (constrainedNetwork) {
      showStaticPoster('save-data');
      return;
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        observer = null;
        loadVideo();
      }, { rootMargin: '400px 0px' });
      observer.observe(root);
    } else {
      loadVideo();
    }

    updateProgress();
  }

  function rootsIn(container) {
    const roots = [];
    if (container.matches && container.matches(SELECTOR)) roots.push(container);
    if (container.querySelectorAll) roots.push(...container.querySelectorAll(SELECTOR));
    return roots;
  }

  function initAll(container = document) {
    rootsIn(container).forEach(initHero);
  }

  function destroyAll(container = document) {
    rootsIn(container).forEach((root) => instances.get(root)?.());
  }

  window[GLOBAL] = { initAll, destroyAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
